export type ProductCardMode = 'large' | 'compact' | 'text';
export type CartWidthPercent = 25 | 28 | 32;
export type CartSide = 'left' | 'right';
export type CartLineMode = 'single' | 'combined';
export type CartSortMode = 'original' | 'organized';
export type CategoryColumns = 5 | 6 | 7;
export type CategoryRows = 1 | 2;
export type QuickDrinkSortMode = 'menu' | 'name' | 'price';
export type QuickDrinkDisplayMode = 'cards' | 'compact';

export interface SmtPresentationPreferences {
  readonly version: 2;
  readonly productCardMode: ProductCardMode;
  readonly cartWidthPercent: CartWidthPercent;
  readonly cartSide: CartSide;
  readonly cartLineMode: CartLineMode;
  readonly cartSortMode: CartSortMode;
  readonly categoryColumns: CategoryColumns;
  readonly categoryRows: CategoryRows;
  readonly quickDrinkEnabled: boolean;
  readonly quickDrinkSortMode: QuickDrinkSortMode;
  readonly quickDrinkDisplayMode: QuickDrinkDisplayMode;
  readonly quickPairingEnabled: boolean;
  readonly showProductImages: boolean;
  readonly showProductCode: boolean;
  readonly showProductDescription: boolean;
}

export const defaultSmtPresentationPreferences: SmtPresentationPreferences = Object.freeze({
  version: 2,
  productCardMode: 'large',
  cartWidthPercent: 28,
  cartSide: 'left',
  cartLineMode: 'single',
  cartSortMode: 'original',
  categoryColumns: 6,
  categoryRows: 1,
  quickDrinkEnabled: true,
  quickDrinkSortMode: 'menu',
  quickDrinkDisplayMode: 'cards',
  quickPairingEnabled: true,
  showProductImages: true,
  showProductCode: false,
  showProductDescription: true,
});

const STORAGE_KEY = 'morefunos.smt.presentation.v2';
const LEGACY_STORAGE_KEY = 'morefunos.smt.presentation.v1';
const PRODUCT_CARD_MODES: readonly ProductCardMode[] = ['large', 'compact', 'text'];
const CART_WIDTHS: readonly CartWidthPercent[] = [25, 28, 32];
const CART_SIDES: readonly CartSide[] = ['left', 'right'];
const CART_LINE_MODES: readonly CartLineMode[] = ['single', 'combined'];
const CART_SORT_MODES: readonly CartSortMode[] = ['original', 'organized'];
const CATEGORY_COLUMNS: readonly CategoryColumns[] = [5,6,7];
const CATEGORY_ROWS: readonly CategoryRows[] = [1,2];
const QUICK_DRINK_SORT_MODES: readonly QuickDrinkSortMode[]=['menu','name','price'];
const QUICK_DRINK_DISPLAY_MODES: readonly QuickDrinkDisplayMode[]=['cards','compact'];

const validMode=(value:unknown):value is ProductCardMode=>PRODUCT_CARD_MODES.includes(value as ProductCardMode);
const validWidth=(value:unknown):value is CartWidthPercent=>CART_WIDTHS.includes(value as CartWidthPercent);
const validSide=(value:unknown):value is CartSide=>CART_SIDES.includes(value as CartSide);
const validCartLineMode=(value:unknown):value is CartLineMode=>CART_LINE_MODES.includes(value as CartLineMode);
const validCartSortMode=(value:unknown):value is CartSortMode=>CART_SORT_MODES.includes(value as CartSortMode);
const validCategoryColumns=(value:unknown):value is CategoryColumns=>CATEGORY_COLUMNS.includes(value as CategoryColumns);
const validCategoryRows=(value:unknown):value is CategoryRows=>CATEGORY_ROWS.includes(value as CategoryRows);
const validQuickDrinkSort=(value:unknown):value is QuickDrinkSortMode=>QUICK_DRINK_SORT_MODES.includes(value as QuickDrinkSortMode);
const validQuickDrinkDisplay=(value:unknown):value is QuickDrinkDisplayMode=>QUICK_DRINK_DISPLAY_MODES.includes(value as QuickDrinkDisplayMode);
const bool=(value:unknown,fallback:boolean)=>typeof value==='boolean'?value:fallback;

export function readSmtPresentationPreferences(): SmtPresentationPreferences {
  if (typeof window === 'undefined') return defaultSmtPresentationPreferences;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SmtPresentationPreferences>;
      if (parsed.version === 2 && validMode(parsed.productCardMode) && validWidth(parsed.cartWidthPercent) && validSide(parsed.cartSide)) {
        return {
          version:2,
          productCardMode:parsed.productCardMode,
          cartWidthPercent:parsed.cartWidthPercent,
          cartSide:parsed.cartSide,
          cartLineMode:validCartLineMode(parsed.cartLineMode)?parsed.cartLineMode:'single',
          cartSortMode:validCartSortMode(parsed.cartSortMode)?parsed.cartSortMode:'original',
          categoryColumns:validCategoryColumns(parsed.categoryColumns)?parsed.categoryColumns:6,
          categoryRows:validCategoryRows(parsed.categoryRows)?parsed.categoryRows:1,
          quickDrinkEnabled:bool(parsed.quickDrinkEnabled,true),
          quickDrinkSortMode:validQuickDrinkSort(parsed.quickDrinkSortMode)?parsed.quickDrinkSortMode:'menu',
          quickDrinkDisplayMode:validQuickDrinkDisplay(parsed.quickDrinkDisplayMode)?parsed.quickDrinkDisplayMode:'cards',
          quickPairingEnabled:bool(parsed.quickPairingEnabled,true),
          showProductImages:bool(parsed.showProductImages,true),
          showProductCode:bool(parsed.showProductCode,false),
          showProductDescription:bool(parsed.showProductDescription,true),
        };
      }
    }
    const legacyRaw=window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if(legacyRaw){
      const legacy=JSON.parse(legacyRaw) as {version?:number;productCardMode?:unknown};
      if(legacy.version===1&&validMode(legacy.productCardMode))return {...defaultSmtPresentationPreferences,productCardMode:legacy.productCardMode};
    }
    return defaultSmtPresentationPreferences;
  } catch {
    return defaultSmtPresentationPreferences;
  }
}

export function writeSmtPresentationPreferences(preferences: SmtPresentationPreferences): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
}
